import { Button, Flex } from "antd";
import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import PermissionCheck from "~Common/PermissionCheck";
import { DataTable } from "~components/DataTable/DataTable.tsx";
import type { DataTableColumn } from "~components/DataTable/types/dataTableColumn.ts";
import { type PermissionNames, PermissionOperations } from "~enums/permissions.ts";
import { useHandlePagination } from "~hooks/useHandlePagination.ts";
import usePermissionsByCompetitionName from "~hooks/usePermissionsByCompetitionName";
import { useSportConfiguration } from "~hooks/useSportConfiguration.ts";
import useFetchEntities from "~pages/Entities/EntitiesListPage/hooks/useFetchEntites.ts";
import { useGetEntitiesTableColumns } from "~pages/Entities/EntitiesListPage/hooks/useGetEntitiesTableColumns.tsx";
import { useGetEntitiesTableFilters } from "~pages/Entities/EntitiesListPage/hooks/useGetEntitiesTableFilters.tsx";
import type { EntityTransformed } from "~pages/Entities/types/entityTransformed.ts";
import { useAddEntityToSeason } from "~pages/SeasonEntities/SeasonEntityListPage/hooks/useAddEntityToSeason.ts";

type Props = {
  existingSeasonEntityIdSet: Set<string>;
  reloadData: () => unknown;
};

export const AddEntityToSeasonDataTable = ({ existingSeasonEntityIdSet, reloadData }: Props) => {
  const { sport } = useParams();
  const { uiConfiguration } = useSportConfiguration();
  const paginationConfig = useHandlePagination(50);
  const [searchText, setSearchText] = useState("");
  const [addingEntityId, setAddingEntityId] = useState<string | null>(null);
  const { seasonEntityPermissionName } = usePermissionsByCompetitionName();
  const seasonEntityPermission = seasonEntityPermissionName as PermissionNames;
  const filterConfig = useGetEntitiesTableFilters({
    status: { value: "ACTIVE", label: "ACTIVE" },
  });
  const { mutateAsync: addEntityToSeason } = useAddEntityToSeason();

  const listParams = useMemo(
    () => ({
      limit: paginationConfig.pageSize.toString(),
      offset: paginationConfig.offset.toString(),
      nameFullLocalContains: searchText,
      gender: filterConfig.currentFilter.gender?.value,
      ageGroup: filterConfig.currentFilter.ageGroup?.value,
      discipline: filterConfig.currentFilter.discipline?.value,
      standard: filterConfig.currentFilter.standard?.value,
      status: filterConfig.currentFilter.status?.value,
      include: ["entityGroups"],
    }),
    [filterConfig.currentFilter, paginationConfig.offset, paginationConfig.pageSize, searchText],
  );

  const { data, isLoading, refetch } = useFetchEntities(listParams, true);
  const entityColumns = useGetEntitiesTableColumns();

  const columns = useMemo<Array<DataTableColumn<EntityTransformed>>>(
    () => [
      ...entityColumns,
      {
        align: "center",
        fixed: "right",
        key: "addEntityToSeason",
        persistent: true,
        width: 50,
        render: (_, row) =>
          existingSeasonEntityIdSet.has(row.entityId) ? (
            <Flex justify="center" align="center">
              <i className="fas fa-check" />
            </Flex>
          ) : (
            <PermissionCheck permissionKeys={[seasonEntityPermission]} permissionOptions={[PermissionOperations.CREATE]}>
              <Button
                onClick={() => {
                  setAddingEntityId(row.entityId);
                  addEntityToSeason(row.entityId)
                    .then(() => {
                      reloadData();
                      refetch();
                    })
                    .finally(() => setAddingEntityId(null));
                }}
                loading={addingEntityId === row.entityId}
                icon={<i className="fas fa-plus" />}
                size="small"
              />
            </PermissionCheck>
          ),
      },
    ],
    [addEntityToSeason, addingEntityId, entityColumns, existingSeasonEntityIdSet, refetch, reloadData, seasonEntityPermission],
  );

  return (
    <DataTable
      columns={columns}
      rowActions={{}}
      rowKey={"entityId"}
      rowNameKey={"nameFullLocal"}
      dataSource={data?.data || []}
      columnChooserLocalStorageKey={`${sport}.tables.settings.columns.addSeasonEntities`}
      isDataLoading={isLoading}
      searchConfig={{
        handleSearch: setSearchText,
        searchText,
      }}
      filtersConfig={filterConfig}
      permissionNames={[seasonEntityPermission]}
      dataTableUiConfiguration={uiConfiguration.pages.entities.listPage}
      paginationConfig={{ ...paginationConfig, hasNext: data?.hasNextLink }}
    />
  );
};
